// Keep in sync with WORKERS/wrangler-edits/hangout/shared/trim-unfinished-sentence.js

const ABBREVIATIONS = new Set(['mr', 'mrs', 'ms', 'dr', 'prof', 'st', 'jr', 'sr', 'vs', 'etc', 'eg', 'ie']);

const CLOSERS = new Set(['"', '\u201D', '\u2019', "'", '\u00BB', '\u300D', '\u300F', '*', ')', ']', '}']);

const SPACED_TERMINATORS = new Set(['!', '?']);

const UNSPACED_TERMINATORS = new Set(['\u2026', '\u3002', '\uFF01', '\uFF1F', '\u061F', '\u0964']);

const hasLetter = (text) => /\p{L}/u.test(text);

const countChar = (text, ch) => {
    let count = 0;
    for (let i = 0; i < text.length; i++) {
        if (text[i] === ch) count += 1;
    }
    return count;
};

const isBalanced = (text) => {
    let straight = 0;
    let stars = 0;
    let guillemet = 0;
    let cjk = 0;
    let cjkDouble = 0;
    let curly = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') straight += 1;
        else if (ch === '*') stars += 1;
        else if (ch === '\u00AB') guillemet += 1;
        else if (ch === '\u00BB') guillemet -= 1;
        else if (ch === '\u300C') cjk += 1;
        else if (ch === '\u300D') cjk -= 1;
        else if (ch === '\u300E') cjkDouble += 1;
        else if (ch === '\u300F') cjkDouble -= 1;
        else if (ch === '\u201C') curly += 1;
        else if (ch === '\u201D') curly -= 1;
        if (guillemet < 0 || cjk < 0 || cjkDouble < 0 || curly < 0) return false;
    }
    return straight % 2 === 0 && stars % 2 === 0 && guillemet === 0 && cjk === 0 && cjkDouble === 0 && curly === 0;
};

const wordBeforePeriod = (text, index) => {
    let i = index - 1;
    while (i >= 0 && /[A-Za-z]/.test(text[i])) i -= 1;
    return text.slice(i + 1, index);
};

const isSkippablePeriod = (text, index) => {
    const prev = index > 0 ? text[index - 1] : '';
    const next = index + 1 < text.length ? text[index + 1] : '';
    if (/\p{N}/u.test(prev) && /\p{N}/u.test(next)) return true;
    const word = wordBeforePeriod(text, index);
    if (word.length === 1) return true;
    return ABBREVIATIONS.has(word.toLowerCase());
};

const closerEnd = (text, terminatorIndex) => {
    let i = terminatorIndex + 1;
    while (i < text.length && CLOSERS.has(text[i])) i += 1;
    return i;
};

const hasBreakAfter = (text, terminatorIndex) => {
    const end = closerEnd(text, terminatorIndex);
    if (end >= text.length) return true;
    if (/\s/u.test(text[end])) return true;
    return UNSPACED_TERMINATORS.has(text[terminatorIndex]);
};

const endsConfidently = (trimmed) => {
    if (!trimmed) return false;
    const last = trimmed[trimmed.length - 1];
    if (last === '*' && countChar(trimmed, '*') % 2 === 0) return true;
    if (last === '\u300D' || last === '\u300F') return true;

    let i = trimmed.length - 1;
    while (i >= 0 && CLOSERS.has(trimmed[i])) i -= 1;
    if (i < 0) return false;

    const ch = trimmed[i];
    if (ch === '\u2026' || UNSPACED_TERMINATORS.has(ch) || SPACED_TERMINATORS.has(ch)) return true;
    if (ch !== '.') return false;

    let start = i;
    while (start > 0 && trimmed[start - 1] === '.') start -= 1;
    const run = i - start + 1;
    if (run === 2) return false;
    if (run >= 3) return true;
    return !isSkippablePeriod(trimmed, i);
};

const findBoundaryEnd = (trimmed) => {
    for (let i = trimmed.length - 1; i >= 0; i -= 1) {
        const ch = trimmed[i];
        if (ch === '.') {
            let start = i;
            while (start > 0 && trimmed[start - 1] === '.') start -= 1;
            const run = i - start + 1;
            if (run === 2) {
                i = start;
                continue;
            }
            if (run === 1 && isSkippablePeriod(trimmed, i)) continue;
            if (!hasBreakAfter(trimmed, i)) {
                i = start;
                continue;
            }
            return closerEnd(trimmed, i);
        }
        if (SPACED_TERMINATORS.has(ch) || ch === '\u2026' || UNSPACED_TERMINATORS.has(ch)) {
            if (!hasBreakAfter(trimmed, i)) continue;
            return closerEnd(trimmed, i);
        }
    }
    return -1;
};

/**
 * Drop a trailing unfinished sentence left by a token-limit cut.
 * Returns the original string unless a confident boundary exists and the tail is at most 10%.
 * @param {string} text
 * @returns {string}
 */
export const trimUnfinishedSentence = (text) => {
    if (typeof text !== 'string' || text.length === 0) return text;
    let end = text.length;
    while (end > 0 && /\s/u.test(text[end - 1])) end -= 1;
    const trimmed = text.slice(0, end);
    if (!trimmed || endsConfidently(trimmed)) return text;

    const boundary = findBoundaryEnd(trimmed);
    if (boundary < 0) return text;

    const kept = trimmed.slice(0, boundary).trimEnd();
    const tail = trimmed.slice(kept.length);
    if (!hasLetter(tail)) return text;
    if (!isBalanced(kept)) return text;
    if ((trimmed.length - kept.length) / trimmed.length > 0.1) return text;
    return kept;
};
